import React, { FC, useState } from 'react';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import {
  AuthWrapper,
  CustomLink,
  MultiInput,
  PageWrapper,
  Text,
  TextButtonTemplate
} from '../../common/components';
import { APP_KEYS } from '../../common/consts';
import { useAuthData } from '../../context/auth.context';
import { toastSuccess } from '../../common/components/toast/toast.component';
import { userService } from '../../common/services/user.service';
import { authService } from '../../common/services/auth.service';
import { checkEmail } from '../utils/auth.util';

interface IProps {}

export const PhoneEmailVerificationPage: FC<IProps> = () => {
  const { push } = useHistory();
  const [serverErrorMsg, setServerErrorMsg] = useState<string>('');
  const { search } = useLocation();
  const isEmail = search.includes('email');
  const loginUser = isEmail
    ? search.split('email=')[1].split('&')[0]
    : search.split('phone=')[1].split('&')[0];

  const { mutate: setVerificateAccount } = useMutation<
    any,
    AxiosError,
    { code: number; emailUser: string | undefined; phoneUser: string | undefined }
  >(
    async ({ code, emailUser, phoneUser }) =>
      userService.setVerificateAccount({ code, emailUser, phoneUser }),
    {
      onSuccess: (res) => {
        if (res.code) {
          toastSuccess(res.message);
          push(`${APP_KEYS.ROUTER_KEYS.LOGIN}`);
        }
      },
      onError: (err: any) => {
        const error = err.response?.data?.message || 'Something went wrong. Try again.';
        setServerErrorMsg(error);
      }
    }
  );

  const handleSetVerificateAccount = (code: number) => {
    setVerificateAccount({
      code,
      emailUser: isEmail ? loginUser : undefined,
      phoneUser: isEmail ? undefined : loginUser
    });
  };

  const { mutate: resetMutationVerificatePhoneEmail } = useMutation<
    any,
    AxiosError,
    { login: string }
  >(
    ({ login }) => {
      const isEmails = checkEmail(login);
      const args = isEmails ? { email: login } : { phone: login };
      return authService.verificatePhoneEmail(args);
    },
    {
      onSuccess: (res) => {
        toastSuccess('Link sent successfully');
      },
      onError: (err: any) => {
        const error = err.response?.data?.message || 'Something went wrong. Try again.';
        setServerErrorMsg(error);
      }
    }
  );
  const handleSentCodeAgain = () => {
    resetMutationVerificatePhoneEmail({ login: loginUser });
  };

  return (
    <PageWrapper justifyContent="center" alignItems="center">
      <AuthWrapper
        title="Enter Verification Code"
        align="center"
        variant="h5"
        errorMsg={serverErrorMsg}
      >
        <Text
          colorVariant="common.black"
          variant="body1"
          align="center"
          fontWeight="fontWeightMedium"
          mt="1.25rem"
        >
          We sent the verification code to
        </Text>
        <Text
          colorVariant="common.black"
          variant="body1"
          align="center"
          fontWeight="fontWeightMedium"
          mb="1.25rem"
        >
          {`${loginUser}`}
        </Text>
        <MultiInput codeHandler={handleSetVerificateAccount} />
        <Text colorVariant="common.black" variant="body1" align="center" mt="1.25rem">
          Didn’t receive the code?
        </Text>
        <TextButtonTemplate onClick={handleSentCodeAgain}>
          <Text
            colorVariant="success.main"
            variant="body1"
            align="center"
            fontWeight="fontWeightBold"
            mt="0.25rem"
          >
            Click here to resend
          </Text>
        </TextButtonTemplate>
        <Text
          colorVariant="common.black"
          variant="subtitle2"
          align="center"
          letterSpacing="0.02em"
          mt="2rem"
        >
          Back to&nbsp;
          <CustomLink to={APP_KEYS.ROUTER_KEYS.LOGIN}>Login</CustomLink>
        </Text>
      </AuthWrapper>
    </PageWrapper>
  );
};
